/**
 * AI 시나리오 플래닝 — 현황 분석 탭 공용 UI
 *
 * 모든 모듈(RP/MP/IP/DP/BF)의 "현황 분석" 탭 섹션을 RP 표준 디자인
 * ("회색 헤더 + 구분선 + 흰 콘텐츠" 카드)으로 통일하기 위한 공용 컴포넌트.
 *
 * (주의) 순환참조 방지: 이 파일은 React/MUI 외에 어떤 *ScenarioPlanning.js 도 import 하지 않는다.
 *   (AiScenarioPlanning.js 가 각 모듈을 top-level import 하므로, 모듈이 공통 화면을
 *    역방향 import 하면 순환이 발생한다. 그래서 공용 UI를 이 독립 파일로 분리한다.)
 */
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@plannel/utils/i18n";
import reduxUtil from "@plannel/utils/redux-util";
import scenarioService from "@plannel/services/ai/scenarioService";
import { Box, Button, Card, CardContent, Chip, CircularProgress, Menu, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis,
  CartesianGrid, Legend, Tooltip as RTooltip,
} from "recharts";
import { AgStyleTooltipContent } from "@plannel/components/rechart/AgStyleTooltip";
import { CollapseArrow } from "@plannel/components/ai/CollapseArrow";
import { FONT } from "@plannel/components/ai/typographyTokens";

// ── 공유 색상 팔레트 (AiScenarioPlanning / RP / MP 와 동일 hex) ──────────────
export const C = {
  acc:    "#185FA5", accL:  "#E6F1FB", accM:  "#378ADD", accS:  "#B5D4F4", accT:  "#0C447C",
  g1:     "#1D9E75", g1l:   "#E1F5EE", g1t:   "#085041", g1s:   "#5DCAA5",
  g2:     "#BA7517", g2l:   "#FAEEDA", g2t:   "#633806", g2s:   "#EF9F27",
  g3:     "#E24B4A", g3l:   "#FCEBEB", g3t:   "#791F1F", g3s:   "#FCA5A5",
  bg:     "#F8F9FB", bg2:   "#FFFFFF", bg3:   "#F1F4F8",
  text1:  "#111827", text2: "#4B5563", text3: "#9CA3AF",
  border: "#E5E7EB",
};

// (공용 접힘/펼침 화살표는 components/ai/CollapseArrow.js — 챗 패널이 쓰는 EvidenceCard 가
//  이 파일(recharts 포함 1,500줄)을 통째로 끌어오던 것을 끊기 위해 분리했다.)

// ── 현황 분석 "문제 항목" 리스트 행 공용 규격 ────────────────────────────────
//   5개 모듈 10개 리스트가 같은 마크업을 인라인 복붙하면서 폭 상수와 헤더 유무가 제각각이었다.
//   규격을 여기 한 곳에 못박는다: 한 줄(이름 + 회색 보조) · 유연폭 바 + 짝 지표 · 우측 정렬 지표 셀 · 우측 칩.
//   (바가 flex 1.4 였을 때 실측 446px — 이름 몫보다 커서 이름이 2줄로 밀려났던 것이 발단.
//    지금은 이름·바가 같은 flex:1 이라 여백을 반씩 나눠 갖고, 이름은 minWidth 0 으로 먼저 양보한다)
//   우측 칩 열도 폭을 고정해야 한다(rightW): 칩 문구가 "주의 5%" / "주의 100%" 로 행마다 달라지면
//   그 차이를 이름 열(flex:1)이 흡수해 바·지표 셀이 행마다 좌우로 밀린다(세로 정렬 깨짐).
// 바 시각 규격 — 트랙 전체 = max, 채움은 좌측 고정. 중립 회색 트랙(트랙에 색을 넣지 않아
//   채움색=상태색만 눈에 남는다) · 채움 0 은 미렌더 · 미세값은 min-width 3px.
//   바 열은 고정폭이 아니라 남는 가운데 여백을 먹는 유연폭이다(flex:1) — 이름 열도 flex:1 이라
//   여백을 반씩 나눠 가지고, 행마다 몫이 같으므로 바 시작 x 는 헤더·전 행에서 그대로 정렬된다.
//   (고정 44px 였을 때는 여백 위에 바가 떠 보여 지표로서의 임팩트가 사라졌다)
export const STATUS_BAR_MIN_W   = 40;   // 유연폭 하한 — 이름이 길어도 바가 이보다 좁아지지 않는다
export const STATUS_BAR_H       = 12;
export const STATUS_BAR_RADIUS  = "3px";
export const STATUS_BAR_TRACK   = "#EEF1F4";
// 바 + 짝 지표를 한 덩어리로 묶는 그룹 간격 — 바가 무엇의 수치인지 붙여 읽히게 한다
export const STATUS_BAR_VAL_GAP = "12px";

// ── 값 정렬 규격 ────────────────────────────────────────────────────────────
//   1) 숫자 = 우측정렬  2) 텍스트 = 좌측정렬  3) 칩 = 중앙정렬
//   4) 숫자+단위는 두 열로 쪼갠다 — 숫자는 우측, 단위는 좌측정렬.
//      한 셀에 "12건"/"3.4%p"/"7일" 을 그대로 담으면 숫자를 우측정렬해도 단위 길이가 달라
//      단위 시작 x 가 행마다 튄다(IP 재고 이슈 진단이 대표 사례). 열을 나누면 실측기가
//      숫자 열·단위 열의 폭을 각각 동기화하므로 단위 시작점이 전 행에서 일치한다.
//   cells / barValue 원소를 { num, unit } 으로 주면 이 2열 렌더가 켜진다({ node } 는 단일 열).
// 숫자 열 ↔ 단위 열 간격 — 일반 열 간격(ml 0.75 = 6px)보다 좁게. 한 값의 두 조각으로 붙여 읽힌다
export const STATUS_UNIT_GAP = "2px";
// 칩 슬롯 간 간격 — 칩이 행마다 0~2개로 갈리는 열은 슬롯 수를 고정해야 정렬이 유지된다
export const STATUS_SLOT_GAP = "4px";

// 바 열(유연폭) 공용 sx — StatusItemBar 와 커스텀 바 노드(MP 누적 세그 바)가 같은 값을 써야
// 리스트 간 바 열 거동이 갈라지지 않는다.
export const STATUS_BAR_COL_SX = { flex: "1 1 auto", minWidth: `${STATUS_BAR_MIN_W}px` };

// 1줄 행 실측 높이 — 패딩 6×2 + 본문 19.5(13px × 1.5) + 테두리 0.5×2 ≈ 33, 행간은 Stack spacing 0.7 = 5.6
export const STATUS_ROW_H   = 33;
export const STATUS_ROW_GAP = 5.6;

/**
 * n행짜리 1줄 리스트의 자연 높이. 나란히 놓인 차트 높이를 리스트에 맞출 때 쓴다
 * (BF 모델 분포 차트가 우측 제품군 리스트에 높이를 맞추는 것과 같은 규칙).
 *
 * @param {number} rows 행 수
 */
export function statusListH(rows) {
  return Math.round(rows * STATUS_ROW_H + Math.max(rows - 1, 0) * STATUS_ROW_GAP);
}

// ── 바 hover 툴팁 ────────────────────────────────────────────────────────────
//   바가 이미 비율을 보여주는데 그 분자·분모(예: "충족 4,200 / 필요 5,000")까지 행에 상시
//   노출하면 같은 지표가 두세 번 반복된다. 상시 표시는 비율(%)과 바로 못 읽는 값(부족량 등)만
//   남기고, 정확한 절대 수치는 이 툴팁으로 내린다.

/** 바 툴팁 공용 MUI Tooltip props — 좁은 nowrap 내용에 맞춘 여백·max-width. */
export const BAR_TIP_PROPS = {
  arrow: true,
  placement: "top",
  componentsProps: { tooltip: { sx: { maxWidth: 260, px: "10px", py: "6px", borderRadius: "6px" } } },
};

/**
 * 바 hover 툴팁 본문 — 바가 나타내는 비율을 절대 수치로 디코드.
 * 수량(EA)·시간(h)·건수·금액 공용 문법(스와치 + 라벨 + 값).
 *
 *   rows = [{ color?, label, value, strong? }]
 *   color 는 바(또는 세그먼트) 색과 일치시킨다 — 색↔의미 매핑이 툴팁의 존재 이유다.
 *   색은 전역 테마 툴팁(크림 배경 + 진한 글자)에 위임한다 — inherit/palette 토큰만 쓰고
 *   하드코딩 색을 넣지 않는다.
 */
export function BarTip({ title, rows = [] }) {
  return (
    <Box sx={{ py: "1px" }}>
      {title && <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: "inherit", mb: "4px", pb: "3px",
        borderBottom: "1px solid", borderColor: "divider", whiteSpace: "nowrap" }}>{title}</Typography>}
      {rows.map((r, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "6px", py: "1px", whiteSpace: "nowrap" }}>
          <Box sx={{ width: 8, height: 8, flexShrink: 0, borderRadius: "2px", bgcolor: r.color || "transparent",
            boxShadow: (theme) => r.color ? `0 0 0 1px ${theme.palette.divider}` : "none" }} />
          <Typography component="span" sx={{ fontSize: FONT.MICRO, color: "text.secondary" }}>{r.label}</Typography>
          <Typography component="span" sx={{ fontSize: FONT.MICRO, fontWeight: r.strong ? 800 : 600, color: "inherit", ml: "auto", pl: "12px", fontVariantNumeric: "tabular-nums" }}>{r.value}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/**
 * 리스트 행 프로그레스 바 — 기본은 유연폭(바 열 전체를 채운다).
 *
 * 값 0 은 채움을 렌더하지 않는다(빈 트랙이 곧 0). 0 이 아닌 아주 작은 값은
 * min-width 3px 로 "0 아님"을 살린다. max 초과분은 100% 로 잘라 그린다(수치는 호출부 실제값).
 *
 * @param {number} value  값. max 와 함께 상대 스케일이 된다
 * @param {string} color  채움 색 (상태색은 호출부 소유 — 이 컴포넌트는 색 판정 안 함)
 * @param {number} max    스케일 상한 (기본 100 = 절대 스케일)
 * @param {number} marker 기준선을 그을 값 (예: 목표 100, 주의 임계 30). 생략 시 마커 없음.
 *                        선만으로는 의미가 전달되지 않는다 — 헤더의 barScale 라벨과 함께 쓸 것
 * @param {string} markerLabel 기준선 hover 툴팁 (무슨 기준인지 한 문장)
 * @param {number} w      고정폭 오버라이드 (생략 시 유연폭)
 * @param {React.ReactNode} tip hover 툴팁 본문 (보통 <BarTip rows=... />). 바가 나타내는
 *                        비율의 분자·분모처럼 행에서 뺀 절대 수치를 여기 담는다.
 *                        ⚠️ 툴팁은 반드시 이 컴포넌트 안에서 트랙 Box 를 감싼다 — 호출부에서
 *                        <Tooltip><StatusItemBar/></Tooltip> 로 감싸면 함수 컴포넌트가 ref 를
 *                        받지 못해 MUI 가 앵커를 잃는다
 * @param {string} ariaLabel 스크린리더용 전문("충족 4,200 / 필요 5,000"). 수치를 툴팁으로
 *                        내린 바는 hover 가 불가능한 사용자를 위해 이 값을 반드시 준다
 */
export function StatusItemBar({ value, color, max = 100, marker, markerLabel, w, tip, ariaLabel }) {
  const safeMax = max > 0 ? max : 100;
  const pct = Math.min(Math.max(((value || 0) / safeMax) * 100, 0), 100);
  const track = (
    <Box sx={{ ...(w ? { flex: `0 0 ${w}px`, width: w } : STATUS_BAR_COL_SX),
               height: STATUS_BAR_H, position: "relative",
               borderRadius: STATUS_BAR_RADIUS, bgcolor: STATUS_BAR_TRACK,
               ...(tip ? { cursor: "default" } : null) }}
         role="progressbar" aria-label={ariaLabel}
         aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
      {pct > 0 && (
        <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0,
                   width: `${pct}%`, minWidth: "3px", bgcolor: color,
                   borderRadius: STATUS_BAR_RADIUS, transition: "width 200ms ease" }} />
      )}
      {/* 기준선 — 채움 위/트랙 위 어디에 놓여도 읽히도록 흰 테두리(boxShadow)를 두른 노치로 그린다.
          단색 얇은 선은 채움색과 겹치면 사라진다. 바 위아래로 3px 삐져나오지만 행 패딩(6px) 안이라
          STATUS_ROW_H 는 그대로다. translateX 로 선 두께의 절반을 되돌려 값 위치에 정확히 맞춘다. */}
      {marker != null && (
        <Box title={markerLabel}
             sx={{ position: "absolute", top: -3, bottom: -3,
                   left: `${Math.min((marker / safeMax) * 100, 100)}%`,
                   transform: "translateX(-50%)",
                   width: "2px", borderRadius: "1px", bgcolor: C.text2,
                   boxShadow: "0 0 0 1px rgba(255,255,255,0.95)" }} />
      )}
    </Box>
  );
  // 빈 title 이면 MUI 가 툴팁을 띄우지 않으므로 tip 없는 호출부에 분기가 필요 없다 —
  // 다만 래퍼 없이 그대로 반환해 DOM 을 늘리지 않는다.
  return tip ? <Tooltip {...BAR_TIP_PROPS} title={tip}>{track}</Tooltip> : track;
}

// ── 열 폭 런타임 동기화 ──────────────────────────────────────────────────────
//   열 폭을 px 상수로 박으면 "가장 긴 번역" 기준이 되어 한국어에서 빈 여백이 남고,
//   반대로 한국어 기준으로 잡으면 영어·베트남어에서 잘린다. 상수 하나로는 둘 다 만족 못 한다.
//   → 리스트 단위로 각 열의 실제 콘텐츠 폭(헤더 라벨 + 전 행의 값)을 재고 최대값으로 맞춘다.
//   언어를 바꾸면 리렌더 → 재측정이라 어느 언어에서도 여백/잘림이 없다.
//   (w / rightW 는 측정 전 첫 페인트와 StatusItemList 로 감싸지 않은 호출부의 폴백으로 남는다)
const StatusColsCtx = createContext(null);

/** 헤더 라벨 공용 캡션 스타일 (헤더 본체와 renderValueCells 가 공유) */
const STATUS_CAP_SX = { fontSize: FONT.CAPTION, color: C.text3, whiteSpace: "nowrap" };

/**
 * 단위 열 기본 스타일 — 값을 읽는 눈이 숫자에 먼저 가도록 단위는 보조 톤으로 낮춘다.
 * (IP/RP 이슈 카드가 쓰던 표기를 전 리스트 공통 규격으로 올린 것)
 * 색·굵기를 값에 맞추고 싶은 열은 cells 원소에 unitSx 로 덮어쓴다.
 */
export const STATUS_UNIT_SX = { fontSize: FONT.CAPTION, color: C.text3, fontWeight: 400, lineHeight: 1 };

/**
 * 한 리스트(헤더 + 행들)를 감싸 열 폭을 공유시킨다. DOM 을 만들지 않으므로 레이아웃 무영향.
 * 감싸지 않아도 동작은 하며, 그 경우 각 셀의 w/rightW 폴백 폭이 쓰인다.
 */
export function StatusItemList({ children }) {
  // colKey → { measure: 자연폭을 재는 inline-block 들, apply: 그 최대폭을 입힐 셀 래퍼들 }
  const reg = useRef(new Map());
  const register = useCallback((key, role, el) => {
    if (!el) return undefined;
    let entry = reg.current.get(key);
    if (!entry) { entry = { measure: new Set(), apply: new Set() }; reg.current.set(key, entry); }
    entry[role].add(el);
    return () => entry[role].delete(el);
  }, []);
  // 실측값을 state 로 두지 않고 DOM style 에 직접 쓴다.
  //   폭은 순수 표현 값이라 React 가 소유할 이유가 없고, state 로 두면 (a) 렌더가 한 번 더 돌고
  //   (b) 의존성 배열을 줄 수 없는 effect 안의 setState 가 되어 정적 분석상 무한 갱신과 구분되지 않는다.
  //   측정 대상은 셀 래퍼가 아니라 그 안의 inline-block 이다 — 래퍼는 폭이 지정되지만 inline-block 은
  //   nowrap 콘텐츠 폭 그대로라, 폭을 입힌 뒤에도 자연폭을 계속 읽을 수 있다(측정↔적용 되먹임 없음).
  //   자식 effect 가 부모보다 먼저 실행되므로 첫 페인트 전에 등록이 끝나 있다.
  useLayoutEffect(() => {
    reg.current.forEach(({ measure, apply }) => {
      let max = 0;
      measure.forEach((el) => {
        if (el?.isConnected) max = Math.max(max, Math.ceil(el.getBoundingClientRect().width));
      });
      // max 0 (측정 대상 없음) 이면 인라인 폭을 지워 sx 폴백(w/rightW)으로 되돌린다
      apply.forEach((el) => { if (el?.isConnected) el.style.width = max > 0 ? `${max}px` : ""; });
    });
  });
  return <StatusColsCtx.Provider value={register}>{children}</StatusColsCtx.Provider>;
}

/**
 * 실측 대상 셀 — 폭은 StatusItemList 가 렌더 후 입힌다. 그 전(또는 미적용 호출부)에는 폴백 w.
 *
 * @param {string} colKey 열 식별자. 헤더와 행이 같은 키를 써야 같은 열로 묶인다
 */
function StatusCell({ colKey, w, align = "right", ml, inner, children }) {
  const register = useContext(StatusColsCtx);
  const ref = useRef(null);
  const outerRef = useRef(null);
  useLayoutEffect(() => {
    if (!register) return undefined;
    const unMeasure = register(colKey, "measure", ref.current);
    const unApply   = register(colKey, "apply", outerRef.current);
    return () => { unMeasure?.(); unApply?.(); };
  }, [register, colKey]);
  return (
    <Box ref={outerRef} sx={{ textAlign: align, flexShrink: 0, width: w, ml }}>
      <Box ref={ref} component="span"
           sx={{ display: inner || "inline-block", whiteSpace: "nowrap",
                 ...(inner ? { alignItems: "center", gap: 0.5 } : null) }}>
        {children}
      </Box>
    </Box>
  );
}

/**
 * 값 없이 열 자리만 차지하는 셀 — 헤더의 우측 칩 열, 헤더의 단위 열처럼
 * "행의 실측 폭은 따르되 자기는 라벨이 없는" 자리. apply 만 등록하고 measure 는 하지 않는다.
 */
function StatusSpacerCell({ colKey = "right", w, ml = 0.75 }) {
  const register = useContext(StatusColsCtx);
  const ref = useRef(null);
  useLayoutEffect(() => (register ? register(colKey, "apply", ref.current) : undefined),
                  [register, colKey]);
  return <Box ref={ref} sx={{ flexShrink: 0, width: w, ml }} />;
}

/**
 * cells / barValue 원소 하나를 셀 노드로 편다.
 *   { num, unit } → 숫자 셀(우측정렬) + 단위 셀(좌측정렬)을 한 덩어리로 묶은 노드
 *   { node }      → 셀 1개 (align 은 원소가 지정, 기본 우측)
 *
 * 쌍은 반드시 자체 flex 래퍼로 묶는다 — 행·헤더 컨테이너가 `gap: 1`(8px), 바 그룹이
 * `gap: 12px` 라, 두 셀을 컨테이너 직접 자식으로 두면 그 gap 이 숫자와 단위 사이에도 끼어
 * "12.5" 와 "%" 가 10~14px 벌어진다. 래퍼 안에서는 STATUS_UNIT_GAP 만 적용된다.
 *
 * 단위 문자열은 여기서 trim 한다 — 언어팩 값이 " cases"(en-US) / " vụ"(vi-VN) 처럼
 * 선행 공백을 품고 있어(기존 값은 수정 금지) 좌측정렬 시 단위 시작 x 가 언어마다 어긋난다.
 * 단위를 ReactNode 로 넘기는 경우의 공백은 호출부 책임.
 *
 * @param {string} colKey 숫자 열 키. 단위 열은 `${colKey}u` 를 쓴다
 * @param {object} c      { node, align, w } 또는 { num, unit, unitSx, w }
 * @param {object} opts   { header: true 면 라벨(c.label)을 그리고 단위 열은 스페이서 }
 */
function renderValueCells(colKey, c, { header = false, ml = 0.75 } = {}) {
  const split = c.unit != null;
  const body  = header
    ? <Typography sx={STATUS_CAP_SX}>{c.label}</Typography>
    : (split ? c.num : c.node);
  if (!split) {
    return <StatusCell key={colKey} colKey={colKey} w={c.w} align={c.align} ml={ml}>{body}</StatusCell>;
  }
  const unitKey = `${colKey}u`;
  return (
    <Box key={colKey} sx={{ display: "flex", alignItems: "center", flexShrink: 0, ml }}>
      <StatusCell colKey={colKey} w={c.w} align={c.align}>{body}</StatusCell>
      {/* 헤더 라벨은 숫자 열에만 등록한다 — 라벨이 숫자보다 길면 숫자 열이 넓어지고
          단위 열은 그 오른쪽에 붙는다(라벨 오른쪽 끝 = 숫자 오른쪽 끝). */}
      {header
        ? <StatusSpacerCell colKey={unitKey} ml={STATUS_UNIT_GAP} />
        : (
          <StatusCell colKey={unitKey} align="left" ml={STATUS_UNIT_GAP}>
            {typeof c.unit === "string"
              ? <Box component="span" sx={{ ...STATUS_UNIT_SX, ...c.unitSx }}>{c.unit.trim()}</Box>
              : c.unit}
          </StatusCell>
        )}
    </Box>
  );
}

/**
 * 리스트 컬럼 라벨 행 — 지표 셀 안의 캡션 줄을 대신한다(행을 진짜 한 줄로 유지).
 * 리스트가 비어 있으면 호출부에서 렌더하지 않는다.
 *
 * @param {React.ReactNode} nameLabel 이름 컬럼 라벨 (예: "품목명 / 거점명")
 * @param {boolean} bar   행에 바가 있으면 true (같은 유연폭 스페이서로 열 맞춤)
 * @param {object}  barValue { w, label, unit } — 바에 붙는 짝 지표 라벨. 행의 barValue 와 폭이 같아야 한다.
 *                  행이 { num, unit } 2열이면 여기도 unit 을 주어야 열 수가 맞는다(값은 안 쓰고 유무만 본다)
 * @param {object}  barScale { max, marks: [{ at, label, title }] } — 바 열 위 눈금 라벨.
 *                  행 바의 marker 와 같은 값(at)을 주면 그 선이 무슨 기준인지 헤더가 설명한다.
 *                  세로선만 그리면 사용자는 의미를 모른다 — marker 를 쓰면 이 라벨도 같이 줄 것
 * @param {Array}   cells [{ w, label, align, unit }] — 행의 cells 와 폭이 1:1 대응해야 한다.
 *                        align 기본 "right"(숫자 정렬). 텍스트 열만 "left" 지정.
 *                        행이 { num, unit } 2열인 셀은 여기도 unit 을 주어야 열 수가 맞는다
 *                        (라벨은 숫자 열에만 들어가고 단위 열은 빈 스페이서가 된다)
 * @param {number|Array} rightW 우측 칩 열 폭. 배열이면 슬롯별 폭(행의 right 배열과 길이가 같아야 한다)
 */
export function StatusItemHeader({ nameLabel, bar = true, barValue, barScale, cells = [], rightW = 30 }) {
  const capSx = STATUS_CAP_SX;
  const rightSlots = Array.isArray(rightW) ? rightW : [rightW];
  const scaleMax = barScale?.max > 0 ? barScale.max : 100;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: "10px", mb: 0.4 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ ...capSx, overflow: "hidden", textOverflow: "ellipsis" }}>
          {nameLabel}
        </Typography>
      </Box>
      {/* 바 + 짝 지표 라벨 그룹 — 행과 동일 구조여야 라벨이 값 위에 정확히 온다 */}
      {bar ? (
        <Box sx={{ flex: "1 1 auto", display: "flex", alignItems: "center", gap: STATUS_BAR_VAL_GAP }}>
          {/* 바 열 자리 — barScale 이 있으면 눈금 라벨을 얹는다. 라벨은 absolute 라 헤더 높이에
              영향이 없다(높이는 이름 열 캡션이 계속 결정). 아래 tick 이 행의 marker 와 x 를 잇는다. */}
          <Box sx={{ ...STATUS_BAR_COL_SX, position: "relative", alignSelf: "stretch" }}>
            {barScale?.marks?.map((m, i) => {
              const left = `${Math.min(Math.max((m.at / scaleMax) * 100, 0), 100)}%`;
              return (
                <Box key={i} title={m.title} sx={{ position: "absolute", left, top: 0, bottom: 0,
                                                   transform: "translateX(-50%)",
                                                   display: "flex", flexDirection: "column",
                                                   alignItems: "center", justifyContent: "flex-end" }}>
                  <Typography sx={{ ...capSx, fontSize: FONT.MICRO, color: C.text2, lineHeight: 1 }}>
                    {m.label}
                  </Typography>
                  <Box sx={{ width: "2px", height: "3px", mt: "1px", bgcolor: C.text2 }} />
                </Box>
              );
            })}
          </Box>
          {barValue && renderValueCells("barValue", barValue, { header: true, ml: 0 })}
        </Box>
      ) : (barValue && renderValueCells("barValue", barValue, { header: true }))}
      {/* 라벨을 자르지 않아도 된다 — 열 폭이 라벨·값 실측 최대값으로 맞춰지기 때문(StatusItemList) */}
      {cells.flatMap((c, i) => renderValueCells(`cell${i}`, c, { header: true }))}
      {/* 우측 칩 열 자리 — 폭은 행의 칩 실측값(헤더는 등록하지 않고 자리만 차지).
          행과 같이 슬롯을 자체 래퍼로 묶는다(컨테이너 gap 이 슬롯 사이에 끼지 않게) */}
      {rightSlots.some((w) => w > 0) && (
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, ml: 0.75 }}>
          {rightSlots.map((w, k) => (
            <StatusSpacerCell key={k} colKey={`right${k}`} w={w} ml={k === 0 ? 0 : STATUS_SLOT_GAP} />
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * 리스트 행 — accent 레일 + 이름(+같은 줄 회색 보조) + 바 + 우측 정렬 지표 셀 + 우측 칩.
 *
 * @param {string} accent 좌측 레일 색 (위험도/달성도 색)
 * @param {React.ReactNode} name 이름 노드 (NavName, 모델 칩 등)
 * @param {React.ReactNode} sub  같은 줄 회색 보조 (거점명 / "품목코드 · 제품군" / 품목수 등).
 *                               폭이 모자라면 이쪽이 먼저 말줄임된다
 * @param {object|React.ReactNode|false} bar StatusItemBar props 객체 또는 커스텀 바 노드(MP 누적 세그 바).
 *                                     null 이면 같은 폭 스페이서만 넣어 열 정렬 유지.
 *                                     false 면 바 열 자체를 없앤다 — 행마다 단위가 달라
 *                                     공통 스케일이 없는 리스트(현황 이슈 카드) 전용
 * @param {object} barValue { w, node } 또는 { w, num, unit } — 바 바로 오른쪽에 붙는 짝 지표.
 *                          바와 한 덩어리로 묶여 렌더된다. 헤더의 barValue.w 와 같은 값을 줄 것
 * @param {Array} cells [{ w, node, align }] 또는 [{ w, num, unit }] — 정렬 규격은 파일 상단 참조.
 *                      숫자(기본) 우측 / 텍스트는 align:"left" / 칩은 align:"center" /
 *                      숫자+단위는 { num, unit } 으로 주면 2열로 쪼개져 단위 시작점이 행 간 정렬된다
 * @param {React.ReactNode|Array} right 우측 칩. 배열이면 슬롯 고정 — 행마다 칩 개수가 갈리는 열은
 *                      배열로 주고 없는 칩 자리에 null 을 넣는다(슬롯별로 폭이 동기화돼 칩이 흔들리지 않는다)
 * @param {number|Array} rightW 우측 칩 열 폭 — 헤더의 rightW 와 같은 값을 줄 것.
 *                        주면 폭 고정(행 간 바·셀 정렬 유지), 생략 시 칩 내용 폭
 */
export function StatusItemRow({ accent, name, sub, bar, barValue, cells = [], right, rightW }) {
  const barNode = bar === false
    ? null
    : bar == null
      ? <Box sx={STATUS_BAR_COL_SX} />
      : (React.isValidElement(bar) ? bar : <StatusItemBar {...bar} />);
  const barValNode = barValue && renderValueCells("barValue", barValue, { ml: 0 });
  // 우측 칩 열 — 배열이면 슬롯 고정(칩이 없는 행도 자리를 지킨다), 단일 노드면 1슬롯
  //   (빈 배열은 슬롯 1개로 취급 — 칩이 하나도 없는 행에서 열이 통째로 사라지면 뒤 열이 밀린다)
  const rightSlots = Array.isArray(right)
    ? (right.length ? right : [null])
    : (right ? [right] : []);
  const rightWs    = Array.isArray(rightW) ? rightW : [rightW];
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: "6px 10px", borderRadius: 1,
               bgcolor: C.bg2, border: `0.5px solid ${C.border}`, borderLeft: `3px solid ${accent}` }}>
      {/* 마우스오버 title 없음 — 이름·보조는 화면 문구를 되풀이할 뿐이라 툴팁이 정보를 더하지 않는다 */}
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.6 }}>
        <Box sx={{ minWidth: 0, flexShrink: 1, overflow: "hidden" }}>{name}</Box>
        {sub != null && sub !== "" && (
          <Typography sx={{ fontSize: FONT.BODY, color: C.text3, flexShrink: 100000, minWidth: 0,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {sub}
          </Typography>
        )}
      </Box>
      {/* 바 + 짝 지표를 한 덩어리로 — 바가 여백에 홀로 떠 있지 않게 수치를 바로 옆에 붙인다 */}
      {barNode ? (
        <Box sx={{ flex: "1 1 auto", display: "flex", alignItems: "center", gap: STATUS_BAR_VAL_GAP }}>
          {barNode}
          {barValNode}
        </Box>
      ) : (barValNode && <Box sx={{ ml: 0.75, display: "flex" }}>{barValNode}</Box>)}
      {cells.flatMap((c, i) => renderValueCells(`cell${i}`, c))}
      {/* 우측 칩 열 — 폭은 전 행의 칩 실측 최대값. 언어가 바뀌면 그 언어 폭으로 다시 맞춰지므로
          한국어에서 남는 여백도, 영어에서 잘림도 없다(rightW 는 측정 전 폴백).
          칩은 슬롯 안에서 중앙정렬 — 슬롯 폭이 행 간 동기화되므로 좌우로 흔들리지 않는다 */}
      {rightSlots.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, ml: 0.75 }}>
          {rightSlots.map((node, k) => (
            <StatusCell key={k} colKey={`right${k}`} w={rightWs[k] ?? rightWs[0]}
                        align="center" ml={k === 0 ? 0 : STATUS_SLOT_GAP} inner="inline-flex">
              {node}
            </StatusCell>
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * 현황 분석 공용 안내(notice) 배너 — amber 톤(정보/주의).
 * 모든 모듈의 "데이터 없음 / 릴리즈 없음" 류 안내 디자인을 통일한다.
 * (코드베이스 표준 amber: bg #FFFBEB / border #FDE68A / text·icon #92400E)
 *
 * @param {React.ReactNode} children 안내 문구 (Typography 내부에 렌더; <b> 등 인라인 강조 가능)
 * @param {React.ReactNode} icon     좌측 아이콘 오버라이드. 기본 InfoOutlinedIcon
 */
export function StatusNotice({ children, icon }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1,
               p: "9px 12px", borderRadius: 1.5,
               bgcolor: "#FFFBEB", border: "0.5px solid #FDE68A" }}>
      {icon ?? <InfoOutlinedIcon sx={{ fontSize: 14, color: "#92400E", flexShrink: 0 }} />}
      <Typography sx={{ fontSize: FONT.SUB, color: "#92400E", lineHeight: 1.5 }}>
        {children}
      </Typography>
    </Box>
  );
}

/**
 * 현황 분석 공용 빈 상태 — "값이 없다"가 아니라 "아직 판정할 수 없다"를 표현한다.
 *
 * 모든 목록·차트 영역의 빈 상태를 이 컴포넌트 하나로 통일한다(점선 보더 + info 아이콘 + 회색 문구).
 * ⚠️ 초록 체크·"이슈 없음"·"정상" 같은 **긍정 신호는 제공하지 않는다** — 집계가 없는 구간을
 *    "문제 없음"으로 읽히게 만들기 때문이다. 톤은 중립(회색) 하나뿐이다.
 *
 * variant 는 표현만 다르고 의미는 같다(빈 상태 = 판정 불가):
 *   - "text"  : 아이콘 + 한 줄 문구           — 리스트/목록
 *   - "rows"  : 스켈레톤 행 count 개 + 문구   — 이슈 목록처럼 "무엇이 채워질지" 예고
 *   - "chart" : 축 점선 + 회색 바 + 문구      — 차트 자리를 지켜 레이아웃이 튀지 않게 한다
 *
 * @param {"text"|"rows"|"chart"} variant  표현 형태 (기본 "text")
 * @param {React.ReactNode} text  안내 문구. 미지정 시 공용 문구(statusNoDataAfterReleaseDesc)
 * @param {number} count          variant="rows" 의 스켈레톤 행 수 (기본 3)
 * @param {number} minHeight      컨테이너 최소 높이(px). 정상 상태와 높이를 맞출 때 사용
 */
export function StatusEmpty({ variant = "text", text, count = 3, minHeight }) {
  const { t } = useTranslation();
  const msg = text ?? t("statusNoDataAfterReleaseDesc");
  const shellSx = {
    border: `1px dashed ${C.border}`, borderRadius: 1.5, bgcolor: "transparent",
    p: "12px 14px", display: "flex", flexDirection: "column", gap: 1,
    ...(minHeight ? { minHeight, justifyContent: "center" } : {}),
  };
  const caption = (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.8 }}>
      <InfoOutlinedIcon sx={{ fontSize: 14, color: C.text3, flexShrink: 0 }} />
      <Typography sx={{ fontSize: FONT.SUB, color: C.text3, lineHeight: 1.5 }}>{msg}</Typography>
    </Box>
  );

  // 스켈레톤은 "여기에 무엇이 들어오는지"를 예고하는 장치라 실제 행/차트와 같은 리듬을 쓴다.
  if (variant === "rows") {
    return (
      <Box sx={shellSx}>
        <Stack spacing={0.8} aria-hidden>
          {Array.from({ length: count }).map((_, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 3, height: 22, borderRadius: 0.5, bgcolor: C.border, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ height: 8, borderRadius: 0.5, bgcolor: C.bg3, width: `${72 - i * 12}%`, mb: 0.6 }} />
                <Box sx={{ height: 6, borderRadius: 0.5, bgcolor: C.bg3, width: `${46 - i * 8}%` }} />
              </Box>
              <Box sx={{ width: 34, height: 8, borderRadius: 0.5, bgcolor: C.bg3, flexShrink: 0 }} />
            </Box>
          ))}
        </Stack>
        {caption}
      </Box>
    );
  }

  if (variant === "chart") {
    const bars = [42, 66, 30, 74, 50, 60];
    return (
      <Box sx={shellSx}>
        <Box aria-hidden sx={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 1.2,
                               px: 0.5, pb: 0.5, minHeight: 96,
                               borderLeft: `1px dashed ${C.border}`, borderBottom: `1px dashed ${C.border}` }}>
          {bars.map((h, i) => (
            <Box key={i} sx={{ flex: 1, height: `${h}%`, borderRadius: "2px 2px 0 0", bgcolor: C.bg3 }} />
          ))}
        </Box>
        {caption}
      </Box>
    );
  }

  return <Box sx={shellSx}>{caption}</Box>;
}

/**
 * 현황 미집계 빈 상태 패널 (전 모듈 공용) — 릴리즈 결과가 없어 현황을 판정할 수 없을 때
 * 화면에서 **가장 강한 위계**로 다음 행동을 제시한다.
 *
 * 기존의 노란 한 줄 배너(StatusNotice)와 '릴리즈 버전 없음' 대체 카드를 이 패널 하나로 대체한다.
 * ⚠️ 이 상태에서 사용자가 할 일은 하나(해당 모듈 계획 실행)이므로 **solid CTA 는 하나만** 둔다.
 *    상단 액션바의 '시나리오 구성으로'는 같은 상태에서 secondary 로 강등된다.
 *
 * @param {React.ReactNode} title  패널 제목
 * @param {React.ReactNode} desc   설명 — 선택 버전명 + 릴리즈하면 무엇이 채워지는지
 * @param {string}   ctaLabel      주 CTA 라벨(계획 실행 화면 이름)
 * @param {Function} onCta         주 CTA 클릭 핸들러
 * @param {Array<{label:string, value:React.ReactNode}>} summary  우측 요약(선택 버전·릴리즈 상태·최근 실행)
 */
export function StatusEmptyPanel({ title, desc, ctaLabel, onCta, summary = [] }) {
  return (
    <Paper variant="outlined"
      sx={{ p: "16px 18px", borderRadius: 2, borderColor: C.accS, bgcolor: C.accL,
            display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
      <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
        <Typography sx={{ fontSize: FONT.TITLE, fontWeight: 700, color: C.text1, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: FONT.SUB, color: C.text2, lineHeight: 1.6, mb: ctaLabel ? 1.5 : 0 }}>
          {desc}
        </Typography>
        {ctaLabel && (
          <Button variant="contained" size="small"
            startIcon={<PlayArrowRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={onCta}
            sx={{ height: 34, px: "14px", borderRadius: "5px", boxShadow: "none",
                  fontSize: FONT.SUB, fontWeight: 600, textTransform: "none",
                  bgcolor: C.acc, "&:hover": { bgcolor: C.accT, boxShadow: "none" } }}>
            {ctaLabel}
          </Button>
        )}
      </Box>
      {summary.length > 0 && (
        <Box sx={{ flex: "0 1 auto", minWidth: 200, pl: { md: 2 },
                   borderLeft: { xs: "none", md: `1px solid ${C.accS}` } }}>
          <Stack spacing={0.6}>
            {summary.map((s) => (
              <Box key={s.label} sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Typography sx={{ fontSize: FONT.BODY, color: C.text3, minWidth: 74, flexShrink: 0 }}>
                  {s.label}
                </Typography>
                <Typography sx={{ fontSize: FONT.BODY, fontWeight: 600, color: C.text2,
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {s.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
}

/**
 * 빈 상태 패널의 주 CTA — 해당 모듈의 계획 실행 화면(탭)으로 이동한다.
 * 이 앱은 SPA 단일 라우트 + 탭 시스템이라 라우팅 대신 redux history 로 탭을 연다.
 *
 * @param {Function} dispatch  useDispatch()
 * @param {string}   viewName  TabMenuList 의 reduxKey (예: "TARGET_INV_SIMULATION", "MP_RUN")
 * @param {object}   viewState 이동과 함께 주입할 초기 상태(선택). 화면마다 키가 다르므로 호출부가 만든다
 */
export function openPlanRunScreen(dispatch, viewName, viewState) {
  dispatch(reduxUtil.removeViewState({ viewName }));
  if (viewState) {
    dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(viewName, viewState)));
  }
  dispatch(reduxUtil.updateHistoryState(viewName));
}

/**
 * 빈 상태 패널의 '최근 실행' 한 줄 — 미집계일 때만 모듈 이력 1건을 조회한다.
 *
 * 전 모듈이 같은 문구·같은 실패 정책을 쓰도록 훅으로 묶는다.
 * 실패·빈 이력이면 null 을 유지해 해당 줄만 '—' 가 되고 패널 자체는 정상 렌더된다
 * (요약 한 줄 때문에 화면을 막지 않는다).
 *
 * @param {string}  moduleCd     "RP" | "IP" | "MP" | "DP" | "BF"
 * @param {boolean} enabled      미집계 여부(true 일 때만 조회)
 * @returns {string|null} "2026. 7. 9. · COMPLETED" 형태 요약 또는 null
 */
export function useLastRunSummary(moduleCd, enabled) {
  const [lastRun, setLastRun] = useState(null);
  useEffect(() => {
    if (!enabled || !moduleCd || lastRun !== null) return;
    let alive = true;
    scenarioService.getHistory(moduleCd, 1)
      .then((res) => {
        // 응답 골격은 {history:[...]} — 구버전/게이트웨이 차이를 흡수해 배열을 찾는다.
        const rows = res?.history ?? res?.requests ?? res?.data?.history ?? res;
        const row  = Array.isArray(rows) ? rows[0] : null;
        if (!alive || !row) return;
        const ts = row.created_ts ? new Date(row.created_ts) : null;
        setLastRun(ts && !isNaN(ts)
          ? `${ts.toLocaleDateString()} · ${row.status_cd ?? ""}`.trim()
          : (row.status_cd ?? null));
      })
      .catch(() => { /* 요약 한 줄 실패는 패널 전체를 막지 않는다 */ });
    return () => { alive = false; };
  }, [moduleCd, enabled]);   // eslint-disable-line react-hooks/exhaustive-deps
  return lastRun;
}

/**
 * 빈 상태 패널의 우측 요약 3줄(선택 버전 · 릴리즈 상태 · 최근 실행) 조립 헬퍼.
 * 모든 모듈이 같은 항목·같은 순서·같은 '—' 표기를 쓰도록 한 곳에서 만든다.
 *
 * @param {Function} t          i18n t
 * @param {string|null} verName 선택(릴리즈) 버전명
 * @param {string|null} releaseTs 릴리즈 일시(이미 지역화된 문자열)
 * @param {string|null} lastRun 최근 실행 기록 요약
 */
export function buildStatusPanelSummary(t, { verName, releaseTs, lastRun }) {
  return [
    { label: t("statusSelectedVersionLabel"), value: verName || KPI_EMPTY_DASH },
    { label: t("statusReleaseStateLabel"),
      value: releaseTs ? t("statusReleasedAtLabel", { date: releaseTs })
                       : t("statusNotReleasedLabel") },
    { label: t("statusLastRunLabel"), value: lastRun || KPI_EMPTY_DASH },
  ];
}

/**
 * 섹션 헤더 우측 "집계 대기" 태그 — 그 섹션이 비어 있는 이유가 "문제 없음"이 아니라
 * "아직 집계 전"임을 헤더 레벨에서 명시한다. 중립 회색만 사용(상태 판정 색 금지).
 */
export function StatusPendingTag() {
  const { t } = useTranslation();
  return (
    <Chip label={t("statusAggregationPendingLabel")} size="small" variant="outlined"
      sx={{ height: 20, fontSize: FONT.BODY, fontWeight: 600, bgcolor: "transparent",
            color: C.text3, border: `1px solid ${C.border}`, "& .MuiChip-label": { px: "8px" } }} />
  );
}

/**
 * 클릭 가능한 명칭 텍스트 — 품목명/자원명 등의 드릴다운 진입점(공용).
 *
 * 이동 규칙(공용): 이동 대상이
 *   - 1개면  → 클릭(또는 Enter/Space) 시 바로 이동(메뉴 없음).
 *   - 2개↑면 → 클릭 시 선택 메뉴(▾)를 띄워 고르게 한다.
 * 대상이 없으면 링크가 아닌 일반 텍스트로 렌더한다. 한 줄 말줄임(ellipsis)은 항상 유지.
 * 타 모듈은 targets(또는 단일 onClick)만 넘기면 동일 규칙이 그대로 적용된다(모듈별 특례 없음).
 *
 * @param {React.ReactNode} children 표시할 명칭
 * @param {Function} onClick  단일 이동 핸들러(대상 1개일 때의 축약형). targets 와 택일
 * @param {Array<{label?: string, desc?: string, icon?: React.ReactNode, onClick?: Function}>} targets  이동 대상 목록.
 *   onClick 이 함수가 아닌(=undefined) 항목은 자동 제외 → 조건부 대상은 삼항으로 undefined 를 넘기면 됨.
 *   icon(좌측 아이콘)·desc(보조 설명)는 선택 — 있으면 메뉴가 아이콘+설명 2줄로 리치하게 렌더된다.
 * @param {string}   color    hover 강조색 (기본 accent). 선택
 * @param {object}   sx       Typography sx 오버라이드 (fontSize/fontWeight 등). 선택
 */
export function NavName({ children, onClick, targets, color = C.acc, sx }) {
  const [anchor, setAnchor] = useState(null);
  // targets 우선, 없으면 onClick 단일. 유효(함수) 핸들러만 남긴다.
  const list = (Array.isArray(targets) ? targets : (onClick ? [{ onClick }] : []))
    .filter((tgt) => typeof tgt?.onClick === "function");
  const clickable = list.length > 0;
  const multi = list.length >= 2;

  const textSx = {
    fontSize: FONT.SUB, fontWeight: 600, color: C.text1,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    ...(clickable && { cursor: "pointer", "&:hover": { color, textDecoration: "underline" } }),
    ...sx,
  };

  const act = (e) => {
    if (!clickable) return;
    e.stopPropagation();
    if (multi) setAnchor(e.currentTarget);   // 2개↑: 선택 메뉴
    else list[0].onClick(e);                 // 1개: 바로 이동
  };
  const onKey = (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); act(e); } };

  // 단일(대상 1개)·비클릭(대상 없음): Typography 하나만 렌더.
  //   1개면 링크처럼 클릭/키보드로 바로 이동, 0개면 핸들러 없는 일반 텍스트.
  if (!multi) {
    return (
      <Typography onClick={clickable ? act : undefined}
        {...(clickable && { role: "link", tabIndex: 0, onKeyDown: onKey })}
        sx={textSx}>
        {children}
      </Typography>
    );
  }

  // 다중(대상 2개↑): 명칭 + ▾ 카레트, 클릭 시 리치 선택 메뉴(아이콘·설명·우측 › + accent 호버).
  return (
    <>
      <Box role="button" tabIndex={0} aria-haspopup="menu" aria-expanded={Boolean(anchor)}
        onClick={act} onKeyDown={onKey}
        sx={{ display: "flex", alignItems: "center", gap: 0.3, minWidth: 0, cursor: "pointer",
          "&:hover .navname-caret": { color: C.acc, bgcolor: C.accL } }}>
        <Typography sx={textSx}>{children}</Typography>
        <Box component="span" aria-hidden className="navname-caret"
          sx={{ flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 15, height: 15, borderRadius: "50%", fontSize: 9, lineHeight: 1,
            color: anchor ? C.acc : C.text3, bgcolor: anchor ? C.accL : "transparent",
            transition: "transform .15s, background-color .15s, color .15s",
            transform: anchor ? "rotate(180deg)" : "none" }}>▾</Box>
      </Box>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: {
          mt: 0.75, minWidth: 210, borderRadius: 2, overflow: "hidden",
          border: `1px solid ${C.border}`, boxShadow: "0 10px 30px rgba(17,24,39,0.14)",
          "& .MuiList-root": { py: 0.5 },
        } }}>
        {list.map((tgt, i) => (
          <MenuItem key={i} disableRipple
            onClick={(e) => { e.stopPropagation(); setAnchor(null); tgt.onClick(e); }}
            sx={{ mx: 0.5, my: 0.15, px: 1, py: 0.75, gap: 1, borderRadius: 1.5, alignItems: "center",
              "&:hover": { bgcolor: C.accL }, "&:hover .navname-go": { color: C.acc } }}>
            {tgt.icon && (
              <Box sx={{ flexShrink: 0, display: "flex", color: C.acc, "& svg": { fontSize: 18 } }}>
                {tgt.icon}
              </Box>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: FONT.SUB, fontWeight: 500, color: C.text1, lineHeight: 1.3,
                whiteSpace: "nowrap" }}>
                {tgt.label ?? `이동 ${i + 1}`}
              </Typography>
              {tgt.desc && (
                <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, lineHeight: 1.35,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {tgt.desc}
                </Typography>
              )}
            </Box>
            <Box component="span" aria-hidden className="navname-go"
              sx={{ flexShrink: 0, fontSize: 16, lineHeight: 1, color: C.text3, transition: "color .15s" }}>›</Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

// ── 카드 본문 최상단 툴바 (현황 분석 차트 카드 공용) ─────────────────────────
// StatusSection 의 헤더 Box 는 전체가 접힘 토글 버튼(onClick + role=button + Enter/Space)이다.
// 그 안에 인터랙티브 컨트롤을 두면 "버튼 안의 버튼"이 되어 히트영역이 겹치고(키보드 Space/Enter
// 는 stopPropagation 으로도 안 막힌다) 드롭다운을 만지려다 섹션이 접히는 오조작이 난다.
// 그래서 컨트롤은 헤더가 아니라 본문 최상단 툴바 행에 둔다 — 본문의 일부라 접으면 함께 숨는다.
export const STATUS_TOOLBAR_H       = 24;  // 컴팩트 Select 고정 높이 (12px 텍스트 + 채움 배경 여백)
export const STATUS_TOOLBAR_GAP_PX  = 8;   // 툴바 ↔ 본문 간격 (StatusSection 의 mb:1)
// 툴바 행이 본문에서 차지하는 총 높이. 본문 높이를 실측으로 맞추는 카드(RP/IP 추이 차트)는
// 차트 높이에서 이만큼 빼야 좌/우 카드 총 높이 정합이 유지된다.
export const STATUS_TOOLBAR_BLOCK_H = STATUS_TOOLBAR_H + STATUS_TOOLBAR_GAP_PX;

/**
 * 툴바 행 — 보조 라벨 + 컨트롤, 우측 정렬.
 * (라벨 폰트/색/간격은 MP 시나리오 드롭다운 라벨과 동일 규격)
 *
 * @param {React.ReactNode} label    좌측 보조 라벨 (FONT.BODY / C.text2)
 * @param {React.ReactNode} children 컨트롤 (StatusToolbarSelect 등)
 */
export function StatusToolbar({ label, children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
      {/* 라벨은 FONT.BODY(12) — CAPTION(11)은 칩·테이블 헤더 등 광범위 사용이라 토큰 자체는 건드리지 않는다 */}
      {label && (
        <Typography sx={{ fontSize: FONT.BODY, fontWeight: 500, color: C.text2, whiteSpace: "nowrap" }}>
          {label}
        </Typography>
      )}
      {children}
    </Box>
  );
}

/**
 * 툴바용 컴팩트 Select — 4개 모듈 추이 카드가 인라인 복붙하던 sx 의 단일 출처.
 * 호출부에서 sx 로 다시 칠하지 말 것(폭만 minWidth 로 조정).
 *
 * 외형은 "소프트 필드형" — MUI 기본 아웃라인(notched fieldset)을 지우고 연회색 채움 + 라운드 6px.
 * 카드 본문(흰 배경) 위에서 컨트롤임이 드러나되 테두리로 시선을 끌지 않게 한다.
 *
 * outside-click / Escape 닫힘은 MUI Select(Modal + ClickAwayListener)가 처리하고, 팝업은
 * 닫힐 때 언마운트되므로(keepMounted 미사용) 리스너도 함께 제거된다 → 커스텀 document
 * 리스너 불필요(이 코드베이스는 outside-click 을 전부 MUI onClose 에 위임한다).
 *
 * @param {string}   value    선택 값 ("" = 전체)
 * @param {Function} onChange (value) => void
 * @param {Array<{value:string,label:string}>} options  첫 항목에 "전체"를 넣어 전달
 * @param {number}   minWidth 트리거 최소 폭 (기본 160)
 */
export function StatusToolbarSelect({ value, onChange, options = [], minWidth = 160, ...rest }) {
  return (
    <TextField
      select
      size="small"
      value={value}
      // 방어적 유지: 이 컨트롤이 클릭 가능한 컨테이너 안에 놓여도 상위 토글을 트리거하지 않게.
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onChange(e.target.value)}
      SelectProps={{
        displayEmpty: true,                        // value="" 도 라벨 렌더
        IconComponent: KeyboardArrowDownRoundedIcon,
        MenuProps: {
          elevation: 0,                            // Paper 기본 그림자 끄고 아래 커스텀 그림자만
          PaperProps: {
            sx: { mt: 0.5, borderRadius: 2, border: `1px solid ${C.border}`,
                  // NavName 메뉴(위)와 동일 그림자 — 페이지 내 팝업 톤 일치
                  boxShadow: "0 10px 30px rgba(17,24,39,0.14)", maxHeight: 320,
                  "& .MuiList-root": { py: 0.5 } },
          },
        },
      }}
      // 고정 높이(STATUS_TOOLBAR_H) + lineHeight 정렬로 텍스트 클리핑 방지
      sx={{ minWidth,
        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
        // 평상시부터 흰 배경 + 연한 하늘색 링. 링은 border 가 아니라 boxShadow 라 두께가 변해도
        // 레이아웃(고정 높이/폭)이 밀리지 않는다.
        "& .MuiInputBase-root": { height: STATUS_TOOLBAR_H, bgcolor: C.bg2, borderRadius: "6px",
          boxShadow: `0 0 0 1px ${C.accS}`, transition: "box-shadow .15s",
          "&:hover": { boxShadow: `0 0 0 1px ${C.accM}` },
          "&.Mui-focused": { boxShadow: `0 0 0 1px ${C.accM}, 0 0 0 3px ${C.accL}` } },
        "& .MuiSelect-select": { py: 0, minHeight: "0 !important", lineHeight: `${STATUS_TOOLBAR_H}px`,
          fontSize: FONT.BODY, fontWeight: 500, color: C.text1,
          pl: "10px", pr: "26px !important" },
        // 열림 시 회전은 MUI 가 .MuiSelect-iconOpen 으로 처리
        "& .MuiSelect-icon": { fontSize: 16, right: 6, top: "calc(50% - 8px)", color: C.text3 } }}
      {...rest}
    >
      {/* 옵션은 Portal 렌더라 카드로 버블링되지 않는다. (MUI SelectInput 이 자식을 cloneElement
          하며 onClick 을 자기 핸들러로 덮으므로 MenuItem 에 stopPropagation 을 달아도 무효) */}
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}
          sx={{ fontSize: FONT.BODY, minHeight: 30, mx: 0.5, px: 1, borderRadius: 1,
                "&.Mui-selected": { bgcolor: C.accL, color: C.acc, fontWeight: 600,
                                    "&:hover": { bgcolor: C.accL } } }}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

// ── 계층별 분포 스택 바 ──────────────────────────────────────────────────────
export const DIST_BAR_H      = 15;    // 바 높이
export const DIST_SEG_GAP    = "2px"; // 세그먼트 간격
export const DIST_SEG_MIN_W  = 3;     // 미세값도 보이게 하는 하한(리스트 바의 min-width 3px 와 동일 규약)
// 램프 최종 단계가 흰색까지 가면 배경과 구분이 사라진다 → 혼합 상한 72%
const DIST_RAMP_MAX_MIX = 0.72;

/**
 * 베이스 색 1개에서 "진 → 연" n단계 램프를 만든다(흰색 혼합).
 * 하드코딩 램프 배열 대신 팔레트 토큰(C.g1/g2/g3)에서 파생시켜, 토큰이 바뀌면 램프도 따라간다.
 *
 * @param {string} baseHex 6자리 hex (#RRGGBB)
 * @param {number} n       단계 수
 * @returns {string[]} 길이 n 의 hex 배열 (index 0 = 가장 진함)
 */
export function distRamp(baseHex, n) {
  const hex = /^#?([0-9a-fA-F]{6})$/.exec(String(baseHex || ""));
  const src = hex ? hex[1] : C.text1.slice(1);
  const rgb = [0, 2, 4].map((i) => parseInt(src.slice(i, i + 2), 16));
  const cnt = Math.max(Number(n) || 0, 1);
  return Array.from({ length: cnt }, (_, i) => {
    const t = cnt === 1 ? 0 : (i / (cnt - 1)) * DIST_RAMP_MAX_MIX;
    return `#${rgb.map((c) => Math.round(c + (255 - c) * t).toString(16).padStart(2, "0")).join("")}`;
  });
}

/**
 * 품목 계층별 분포 스택 바 — 헤더 1줄 + 유연폭 세그먼트 바 + 하단 2줄 라벨.
 *
 * 세그먼트/라벨이 **동일한 flex 비율**(flexGrow=value, flexBasis 0, 같은 gap·minWidth)로 놓여
 * 라벨이 자기 세그먼트 아래 정확히 정렬된다. 좁아서 잘린 라벨은 툴팁으로 읽는다.
 *
 * 라벨 2줄은 분류명·수량 모두 FONT.BODY — 11px 는 가독성이 떨어져 쓰지 않는다.
 * 두 줄 구분은 크기가 아니라 색(text3/text1)·굵기(400/600)로 준다.
 *
 * @param {React.ReactNode} title    섹션 제목
 * @param {React.ReactNode} tip      제목 옆 ⓘ 툴팁 (분류 기준 등). DP DpBlockHeading 과 동일 규격
 * @param {React.ReactNode} subtitle 보조 설명 (제목 오른쪽)
 * @param {string} baseColor         램프 베이스 색 토큰 (C.g3/g2/g1)
 * @param {React.ReactNode} summary  우측 합계 노드 (문자열이면 tabular-nums 로 렌더)
 * @param {Array<{name:string,cd:string,value:number,is_etc:boolean,etc_cnt:number}>} segments
 *        백엔드 순서(값 내림차순, 기타 마지막)를 그대로 신뢰한다
 * @param {Function} formatValue     (value) => string. 수량 표기 (기본 toLocaleString)
 * @param {string}   unitLabel       툴팁 수량 뒤 단위 (예: "EA")
 * @param {Function} etcLabel        (count) => string. '기타 N개 분류' 문구(i18n 은 호출부 책임)
 * @param {Function} onSegmentClick  (seg) => void. cd 가 있는 세그먼트만 클릭 가능.
 *        바·라벨 어느 쪽을 눌러도 동일하게 호출된다(토글 여부는 호출부 책임)
 * @param {string} selectedCd        선택된 세그먼트 cd. 지정 시 나머지는 흐려지고 선택분만 강조된다
 * @param {React.ReactNode} emptyText 세그먼트가 없을 때 안내 문구
 */
export function StatusDistributionBar({
  title, tip, subtitle, baseColor, summary, segments = [],
  formatValue, unitLabel = "", etcLabel, onSegmentClick, selectedCd, emptyText,
}) {
  const fmt = formatValue || ((v) => Number(v || 0).toLocaleString());
  const total = segments.reduce((s, seg) => s + Number(seg.value || 0), 0);
  // 라벨 툴팁을 **자기 세그먼트 바**에 앵커링하기 위한 ref (아래 tipProps 설명 참조).
  //   ⚠️ 훅이라 아래 빈 상태 early return 보다 앞에 있어야 한다.
  const barRefs = useRef([]);
  // 램프는 '기타'를 제외한 실 분류 수만큼 — 기타는 항상 중립 회색이라 램프 단계를 소비하지 않는다
  const ramp = distRamp(baseColor, segments.filter((s) => !s.is_etc).length);

  const header = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: 0.8 }}>
      <Box sx={{ width: 3, height: 13, borderRadius: "2px", bgcolor: baseColor, flexShrink: 0 }} />
      <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.text1 }}>{title}</Typography>
      {tip && (
        <Tooltip title={tip} placement="top" arrow>
          <InfoOutlinedIcon sx={{ fontSize: 13, color: C.text3, cursor: "help" }} />
        </Tooltip>
      )}
      {subtitle && <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>{subtitle}</Typography>}
      {summary != null && (
        <Typography sx={{ ml: "auto", fontSize: FONT.BODY, fontWeight: 600, color: C.text2,
                          fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {summary}
        </Typography>
      )}
    </Box>
  );

  if (segments.length === 0) {
    // 빈 상태 표현은 공용 StatusEmpty 로 통일한다(점선 보더 + info 아이콘 + 회색 문구).
    return (
      <Box>
        {header}
        <StatusEmpty text={emptyText} />
      </Box>
    );
  }

  // 세그먼트/라벨 공용 flex 규격 — 두 행이 같은 값을 써야 라벨이 세그먼트 아래 정렬된다
  const flexSx = (seg) => ({
    flexGrow: Math.max(Number(seg.value || 0), 0),
    flexBasis: 0,
    minWidth: `${DIST_SEG_MIN_W}px`,
  });
  const segColor = (seg, rampIdx) => (seg.is_etc ? C.border : (ramp[rampIdx] || baseColor));
  const segName  = (seg) => (seg.is_etc ? etcLabel?.(seg.etc_cnt ?? 0) : seg.name);
  const segTip   = (seg) => {
    const pct = total > 0 ? ((Number(seg.value || 0) / total) * 100).toFixed(1) : "0.0";
    return `${segName(seg) || "—"} · ${fmt(seg.value)}${unitLabel ? ` ${unitLabel}` : ""} · ${pct}%`;
  };

  // 램프 인덱스는 '기타'를 건너뛰며 증가 — 기타가 중간에 오더라도 색 순서가 밀리지 않는다
  let rampIdx = -1;
  const withIdx = segments.map((seg) => {
    if (!seg.is_etc) rampIdx += 1;
    return { seg, idx: seg.is_etc ? -1 : rampIdx };
  });

  // 선택 상태 — 선택이 있으면 나머지를 흐려 대비를 만든다. 선택분은 아웃라인으로 표시하되
  // 세그먼트 높이·flex 규격은 건드리지 않는다(바/라벨 정렬이 두 행의 동일 규격에 걸려 있다).
  const hasSel   = Boolean(selectedCd);
  const isSel    = (seg) => hasSel && seg.cd === selectedCd;
  const dimSx    = (seg) => (hasSel && !isSel(seg) ? { opacity: 0.3 } : null);
  const canClick = (seg) => Boolean(seg.cd) && Boolean(onSegmentClick);

  // 툴팁 공통 규격.
  //   - disableInteractive: 툴팁 팝퍼가 포인터 이벤트를 먹지 않게 한다. MUI 기본값은 interactive
  //     라 팝퍼가 뜬 자리의 클릭이 팝퍼로 흡수된다(바를 누르려다 툴팁을 누르게 된다).
  //   - anchorEl: 라벨 툴팁을 **자기 세그먼트 바** 기준으로 띄운다. 라벨 기준 placement="top" 이면
  //     라벨 바로 위 = 바가 있는 자리에 뜨면서 바를 가려 클릭을 방해했다. 바에 앵커링하면
  //     바보다 더 위에 떠서 바가 항상 노출된다(바 자신의 툴팁 위치와도 일치).
  const tipProps = (i) => ({
    arrow: true,
    placement: "top",
    disableInteractive: true,
    PopperProps: { anchorEl: () => barRefs.current[i] },
  });

  return (
    <Box>
      {header}
      <Box sx={{ display: "flex", gap: DIST_SEG_GAP }}>
        {withIdx.map(({ seg, idx }, i) => (
          <Tooltip key={`b-${seg.cd || i}`} title={segTip(seg)} {...tipProps(i)}>
            <Box
              ref={(el) => { barRefs.current[i] = el; }}
              onClick={canClick(seg) ? () => onSegmentClick(seg) : undefined}
              sx={{ ...flexSx(seg), height: DIST_BAR_H, borderRadius: "2px",
                    bgcolor: segColor(seg, idx), ...dimSx(seg),
                    ...(isSel(seg) && { outline: `1.5px solid ${C.text1}`, outlineOffset: "1px" }),
                    ...(canClick(seg) && { cursor: "pointer" }) }} />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: DIST_SEG_GAP, mt: 0.6 }}>
        {withIdx.map(({ seg }, i) => {
          const clickable = canClick(seg);
          const sel = isSel(seg);
          return (
            <Tooltip key={`l-${seg.cd || i}`} title={segTip(seg)} {...tipProps(i)}>
              <Box
                onClick={clickable ? () => onSegmentClick(seg) : undefined}
                sx={{ ...flexSx(seg), ...dimSx(seg), ...(clickable && { cursor: "pointer",
                      "&:hover .distSegName": { color: C.acc, textDecoration: "underline" } }) }}>
                <Typography className="distSegName"
                  sx={{ fontSize: FONT.BODY, color: sel ? C.text1 : C.text3, lineHeight: 1.3,
                        fontWeight: sel ? 700 : 400,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {segName(seg)}
                </Typography>
                <Typography
                  sx={{ fontSize: FONT.BODY, fontWeight: 600, color: C.text1, lineHeight: 1.3,
                        fontVariantNumeric: "tabular-nums",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {fmt(seg.value)}{unitLabel ? ` ${unitLabel}` : ""}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

// ── 인사이트 카드 — 도트 라벨 + 제목 + 라벨 블록 ──────────────────────────────
// 주요 이슈(현황 분석)와 권장 대책(시나리오 구성)이 같은 껍데기를 쓴다. 복제하면 규격이 조용히 갈라진다.
export const STATUS_CARD_GRID_SX = {
  display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 0.8, alignItems: "stretch",
};

/**
 * 카드 껍데기 — 도트+분류 라벨, 제목, 본문 블록.
 * 색은 호출부가 주입한다(심각도·파라미터 그룹은 별개 축이라 여기서 고르면 한 팔레트에 묶인다).
 *
 * @param {{color: string, text: string}} accent 도트 색 / 라벨 글자색
 * @param {React.ReactNode} eyebrow 분류 라벨 (증상·파라미터 그룹). 없으면 도트 줄 자체를 생략
 * @param {React.ReactNode} title   카드 제목
 * @param {boolean} dim             참고용 카드(불투명도 낮춤)
 */
export function StatusInsightCard({ accent, eyebrow, title, dim, children }) {
  return (
    <Box sx={{ border: `1px solid ${C.border}`, borderRadius: "8px", bgcolor: C.bg2,
      p: "10px 12px 11px", display: "flex", flexDirection: "column", gap: 0.9,
      minWidth: 0, opacity: dim ? 0.8 : 1 }}>
      {/* 카드 배경 틴트를 뺐으므로 도트 색이 유일한 분류 신호다(대문자 강조는 CJK 에 없어 안 쓴다) */}
      {eyebrow && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Box sx={{ width: 5, height: 5, borderRadius: "50%", flexShrink: 0,
            bgcolor: accent?.color ?? C.text3 }} />
          <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: accent?.text ?? C.text3 }}>
            {eyebrow}
          </Typography>
        </Box>
      )}
      {title && (
        <Typography sx={{ fontSize: FONT.H2, fontWeight: 700, color: C.text1, lineHeight: 1.35 }}>
          {title}
        </Typography>
      )}
      {children}
    </Box>
  );
}

// '대상' 그릇 — 주요 이슈(칩)와 권장 대책(행)이 같은 테두리·배경을 쓴다.
//   한쪽만 테두리가 있으면 같은 '대상' 인데 다른 종류의 정보로 읽힌다.
export const STATUS_TARGET_CHIP_SX = {
  bgcolor: C.bg3, border: `0.5px solid ${C.border}`, borderRadius: "6px", p: "3px 7px", minWidth: 0,
};

// 라벨을 값 위에 쌓는다 — 폭 고정 인라인 라벨은 라벨이 긴 언어에서 값이 밀린다.
export function StatusCardField({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text3, mb: 0.3 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * RP식 "회색 헤더 + 구분선 + 흰 콘텐츠" 섹션 카드 골격.
 * 프레임만 통일하고 내부 콘텐츠(children)는 호출부가 그대로 주입한다.
 *
 * @param {React.ReactNode} icon    헤더 좌측 아이콘 엘리먼트 (예: <BarChartIcon sx={{fontSize:15,color:C.acc}} />)
 * @param {React.ReactNode} title   헤더 제목 (fontSize 12 / 700). 문자열 또는 노드
 * @param {React.ReactNode} subtitle 헤더 부제 ("— ..." 형태, fontSize 10 / text3). 선택
 * @param {React.ReactNode} right   헤더 우측 요소 (칩/로딩 스피너 등). ml:auto 는 내부에서 처리.
 *                                  헤더 전체가 접힘 토글이므로 인터랙티브 컨트롤은 넣지 말고 toolbar 를 쓴다
 * @param {React.ReactNode} toolbar 본문 최상단 툴바 행 (StatusToolbar). 본문의 일부라 접으면 함께 숨는다. 선택
 * @param {boolean} disableContentPadding  true면 CardContent padding 0 (테이블 등). contentSx 보다 우선
 * @param {object}  contentSx       CardContent sx 오버라이드 (섹션별 padding 차등)
 * @param {object}  cardSx          Card sx 오버라이드 (height:"100%" 등)
 * @param {string}  variant         Card variant (기본 "outlined")
 * @param {boolean} collapsible     true면 헤더 클릭으로 콘텐츠 접기/펼치기. 선택
 * @param {boolean} defaultOpen     collapsible일 때 초기 펼침 여부 (기본 true). 선택
 * @param {React.ReactNode} children 기존 섹션 콘텐츠 JSX
 */
export function StatusSection({
  title, subtitle, right, toolbar,
  disableContentPadding = false, contentSx, cardSx, variant = "outlined",
  collapsible = false, defaultOpen = true,
  // collapseDisabled: 접기/펼치기를 잠근다(펼친 상태 고정, 화살표 숨김).
  //   내용이 빈 상태(안내 문구)뿐일 때 사용 — 접으면 그 안내까지 사라져 화면이 이유 없이 비어 보인다.
  collapseDisabled = false,
  // onHeaderClick: 외부에서 열림/닫힘을 제어할 때(예: 커스텀 접힘 요약을 쓰는 섹션) 헤더 클릭 콜백.
  //   collapsible(내부 상태 토글)과 배타적. open 은 헤더 화살표 방향(회전) 표시에만 사용.
  onHeaderClick, open: openProp = true,
  children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const canCollapse = collapsible && !collapseDisabled;
  // 헤더 클릭 가능 여부 = 내부 접기 또는 외부 onHeaderClick 제공.
  const clickable = !collapseDisabled && (canCollapse || Boolean(onHeaderClick));
  const handleHeader = canCollapse ? () => setOpen(v => !v) : (collapseDisabled ? undefined : onHeaderClick);
  // 화살표 회전 방향: 내부 토글이면 내부 open, 외부 제어면 openProp.
  const headerOpen = canCollapse ? open : openProp;
  const contentPad = disableContentPadding
    ? { p: "0 !important" }
    : { p: "14px 15px !important", ...contentSx };
  return (
    <Card variant={variant} sx={{ borderColor: C.border, borderRadius: 2, ...cardSx }}>
      <Box onClick={clickable ? handleHeader : undefined}
           {...(clickable && {
             role: "button", tabIndex: 0, "aria-expanded": headerOpen,
             onKeyDown: (e) => {
               if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleHeader?.(); }
             },
           })}
           sx={{ p: "10px 15px", bgcolor: C.bg3, borderBottom: `0.5px solid ${C.border}`,
                 display: "flex", alignItems: "center", gap: 1,
                 ...(clickable && { cursor: "pointer", userSelect: "none",
                                      "&:hover": { bgcolor: C.bg } }) }}>
        <Typography sx={{ fontSize: FONT.TITLE, fontWeight: 700, color: C.text1 }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>{subtitle}</Typography>}
        {right && <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>{right}</Box>}
        {canCollapse && <CollapseArrow open={open} ml={right ? 0.5 : "auto"} />}
      </Box>
      {(!canCollapse || open) && (
        <CardContent sx={contentPad}>
          {/* 툴바는 본문의 일부 — 접으면 함께 숨는다. 간격은 STATUS_TOOLBAR_GAP_PX(=mb 1)와 동일 */}
          {toolbar && <Box sx={{ mb: 1 }}>{toolbar}</Box>}
          {children}
        </CardContent>
      )}
      {/* collapsible=false 이면 CardContent 는 항상 렌더 — 외부(onHeaderClick) 제어 섹션은 자체적으로 접힘 UI 관리 */}
    </Card>
  );
}

/** KPI 값이 없을 때(미집계) 표시하는 문자 — 전 모듈 단일 표기. */
export const KPI_EMPTY_DASH = "—";

/**
 * RP 표준 KPI 카드 — 연한 배경 + 좌측 라벨 → 큰 숫자(+단위) → 하단 목표/추세.
 * 모든 모듈의 현황 KPI 카드를 이 컴포넌트로 통일한다(색상·문구는 호출부 주입).
 *
 * ⚠️ 값 표기 규칙(전 모듈 공통, 이 컴포넌트가 소유):
 *   value 가 null/undefined 이면 "미집계"(판정 불가)로 간주해
 *     - 값은 "—", 글자색은 중립(text3)
 *     - 단위(%, EA, 일)를 표시하지 않는다 — 없는 값에 단위를 붙이면 실측 0 처럼 읽힌다
 *     - valueRight(목표 대비 델타 배지)를 렌더하지 않는다 — 비교 대상이 없다
 *     - 하단 상태색 바를 중립 회색으로 바꾼다 — 빨강/초록은 판정이 섰다는 신호다
 *   값 0 은 **실측값**이므로 위 규칙을 적용하지 않는다(단위·색·배지 모두 정상 표시).
 *   → 호출부는 값이 없을 때 "-"/"--"/0 같은 대체 문자열을 만들지 말고 null 을 넘긴다.
 *
 * @param {React.ReactNode} label    상단 라벨 (fontSize 10 / text3)
 * @param {React.ReactNode} value    큰 숫자 (fontSize 26 / 700). null 이면 미집계 표기
 * @param {React.ReactNode} unit     숫자 옆 단위 (fontSize 12). 선택 — value에 단위가 포함됐으면 생략
 * @param {string} color             값·단위 색
 * @param {React.ReactNode} footer   하단 좌측 보조 텍스트 (목표/부가 지표). 없으면 "—"
 * @param {React.ReactNode} footerLeft 하단 좌측 노드 (배지 등). 주어지면 footer 문자열 대신
 *                                     Typography 래핑 없이 그대로 렌더(div-in-p 방지). 선택
 * @param {React.ReactNode} footerRight 하단 우측 요소 (추세 아이콘·상태 라벨 등). 선택
 * @param {boolean} loading          true면 값 자리에 스피너
 */
export function StatusKpiCard({
  label, value, unit, color = C.acc,
  footer, footer2, footerLeft, footerRight, valueRight, loading = false,
}) {
  // 과도한 채색 제거: 흰 배경 + 중립 회색 테두리(구분감)로 통일하고, 상태는 값 글자색 +
  // 하단 상태색 바로만 표현한다.
  // valueRight: 값 우측 인라인 노드(목표 대비 달성 배지 등).
  const empty     = value === null || value === undefined;
  const valColor  = empty ? C.text3  : color;
  const barColor  = empty ? C.border : color;
  return (
    <Paper variant="outlined"
      sx={{ position: "relative", overflow: "hidden",
            p: "12px 14px", borderRadius: 1.5, borderColor: C.border, bgcolor: C.bg2 }}>
      <Typography sx={{ fontSize: FONT.BODY, color: C.text2, fontWeight: 600, mb: 0.5 }}>{label}</Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mb: 0.5 }}>
        {loading
          ? <CircularProgress size={16} sx={{ color }} />
          : <Typography sx={{ fontSize: FONT.KPI, fontWeight: 700, color: valColor, lineHeight: 1 }}>
              {empty ? KPI_EMPTY_DASH : value}
            </Typography>}
        {/* 언어팩 값이 " cases"(en-US) / " mục"(vi-VN) 처럼 선행 공백을 품고 있어 행의 gap(4px)에 더해진다 */}
        {unit && !loading && !empty && (
          <Typography sx={{ fontSize: FONT.TITLE, color: valColor, fontWeight: 600, lineHeight: 1 }}>
            {typeof unit === "string" ? unit.trim() : unit}
          </Typography>
        )}
        {valueRight && !loading && !empty && (
          <Box sx={{ ml: 0.5, minWidth: 0, display: "flex", alignItems: "baseline" }}>{valueRight}</Box>
        )}
        {/* baseline 높이 기준선 — valueRight(목표 대비 배지) 유무와 무관하게 value 행 높이를 통일.
            배지가 없는 카드(MP 등)도 배지와 동일한 12px·lineHeight 1.4 라인박스를 항상 포함시켜
            IP/RP(배지 有)와 카드 높이가 정확히 일치하도록 한다. */}
        <Box aria-hidden sx={{ width: 0, overflow: "hidden", fontSize: FONT.BODY, lineHeight: 1.4 }}>&#8203;</Box>
      </Box>
      {(footer || footerLeft || footerRight) && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5 }}>
          {footerLeft
            ? <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 0.3 }}>{footerLeft}</Box>
            : footer
            ? <Typography sx={{ fontSize: FONT.BODY, color: C.text2, fontWeight: 500, lineHeight: 1.4,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {footer}
              </Typography>
            : <span />}
          {footerRight && (
            <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.3 }}>{footerRight}</Box>
          )}
        </Box>
      )}
      {/* 2번째 서브라인(규모: 수량·금액). footer2 를 넘긴 소비처만 렌더 — 빈 문자열("")도
          높이 고정용으로 렌더(공백 문자)해 카드 서브 블록 높이를 통일한다. 미전달(undefined)이면 미렌더(기존 화면 무변). */}
      {footer2 != null && (
        <Typography sx={{ fontSize: FONT.BODY, color: C.text2, fontWeight: 500, lineHeight: 1.4,
          minHeight: "1.4em",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {footer2 || " "}
        </Typography>
      )}
      {/* 하단 상태색 바 — 미집계면 중립 회색(상태 판정이 없다는 뜻) */}
      <Box sx={{ position: "absolute", left: 0, bottom: 0, width: "100%", height: 3, bgcolor: barColor }} />
    </Paper>
  );
}

/**
 * KPI 카드 그리드 — StatusKpiCard 들을 N열 그리드로 배치.
 * "현황 분석" 메인 섹션은 StatusKpiSection 이 내부에서 사용하고,
 * 그 외 보조 섹션(예: 정확도)은 StatusSection 과 함께 직접 사용한다.
 *
 * @param {number} columns  열 수 (기본 4)
 * @param {Array}  cards    StatusKpiCard props 배열 (각 항목에 key 권장). 빈 배열이면 null 렌더
 */
export function KpiCardGrid({ columns = 4, cards = [] }) {
  if (!cards.length) return null;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 1.5 }}>
      {cards.map((c, i) => <StatusKpiCard key={c.key ?? i} {...c} />)}
    </Box>
  );
}

/**
 * 현황 분석 기준 버전 배지 — "현황 분석" 섹션 헤더 우측에 표시.
 * (이전에는 화면 최상단 별도 배너였으나 섹션 헤더로 통합. 라벨은 섹션 제목과
 *  중복되므로 생략하고 버전 Chip + 릴리즈 날짜만 노출.)
 *
 * @param {object|null|undefined} versionInfo  latestVersions[mod.code]
 *   - undefined/null: 조회 중(스피너) · found=false: 미표시(null) · found: 버전 Chip
 */
export function StatusVersionBadge({ versionInfo }) {
  const { t } = useTranslation();
  if (versionInfo === undefined || versionInfo === null) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
        <CircularProgress size={11} sx={{ color: C.acc }} />
        <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>{t("versionLoadingLabel")}</Typography>
      </Box>
    );
  }
  if (!versionInfo.found) return null;
  const relTs = versionInfo.releaseTs
    ? new Date(versionInfo.releaseTs).toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" })
    : null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap", justifyContent: "flex-end" }}>
      <Chip label={versionInfo.name} size="small" variant="outlined"
        sx={{ height: 20, fontSize: FONT.BODY, fontWeight: 700, bgcolor: "transparent", color: C.acc,
              border: `1px solid ${C.accS}`, "& .MuiChip-label": { px: "8px" } }} />
      {relTs && <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>{t("releaseDateLabel", { date: relTs })}</Typography>}
    </Box>
  );
}

/**
 * 현황 분석 메인 섹션 — 모든 모듈 공통의 "현황 분석" 섹션 골격.
 * 헤더(아이콘·제목·설명 문구)는 이 컴포넌트가 고정하고, 각 모듈은 cards 데이터(와 고유
 * children)만 넘긴다. 아이콘/제목/설명을 외부에서 주입하지 않아 모든 모듈의 현황 분석
 * 섹션 헤더·여백이 완전히 동일하게 유지된다.
 *
 * @param {React.ReactNode} right  헤더 우측 상태 표시(로딩 스피너 등). 선택
 * @param {number} columns      KPI 그리드 열 수 (기본 4)
 * @param {Array}  cards        StatusKpiCard props 배열 (각 항목에 key 권장). 빈 배열이면 그리드 생략
 * @param {React.ReactNode} empty  KPI 데이터가 없을 때 카드 위에 표시할 안내 (선택)
 * @param {React.ReactNode} children  KPI 그리드 아래 모듈 고유 콘텐츠(분포바·리스크 칩 등). 선택
 * @param {number} spacing      empty/그리드/children 사이 세로 간격 (기본 1.5)
 * @param {boolean} collapsible  헤더 클릭 접기/펼치기 (StatusSection 위임). 선택
 * @param {boolean} defaultOpen  collapsible일 때 초기 펼침 (기본 true). 선택
 */
export function StatusKpiSection({
  right, versionInfo,
  columns = 4, cards = [], empty = null, children = null, spacing = 1.5,
  collapsible = false, defaultOpen = true,
}) {
  const { t } = useTranslation();
  // 헤더 우측: 기준 버전 배지(있으면) + 기존 right(로딩 스피너 등) 합성.
  const hasVersionSlot = versionInfo !== undefined;
  const headerRight = (hasVersionSlot || right) ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {hasVersionSlot && <StatusVersionBadge versionInfo={versionInfo} />}
      {right}
    </Box>
  ) : undefined;
  return (
    <StatusSection
      title={t("stepSituationAnalysis")}
      subtitle={t("situationAnalysisSubtitle")}
      right={headerRight}
      collapsible={collapsible}
      defaultOpen={defaultOpen}
    >
      <Stack spacing={spacing}>
        {empty}
        <KpiCardGrid columns={columns} cards={cards} />
        {children}
      </Stack>
    </StatusSection>
  );
}

// ── 미니 스파크라인 (축·눈금 없는 초소형 추이선) ────────────────────────────
//   값 배열을 폭 w 안에 자기 min/max 로 정규화해 그린다. 2점 미만이면 렌더하지 않는다.
//   splitAt 을 주면 두 구간으로 나뉜다 — [0..splitAt] 과 [splitAt..끝].
//     BF  : 과거/미래 경계  → emphasis="head" (과거=실선 강조 / 미래=점선 흐림)
//     DP  : 직전3M/최근3M 경계 → emphasis="tail" (직전=흐림 / 최근=실선 강조)
//   recharts 를 쓰지 않는 이유: 축·툴팁·ResponsiveContainer 가 전부 불필요하고,
//   카드 안 20~30px 높이에서는 SVG polyline 2줄이 더 가볍고 정확하다.
const SPARK_EMPHASIS = {
  head: (color, muted) => [{ c: color, dash: null }, { c: muted, dash: "3 2" }],
  tail: (color, muted) => [{ c: muted, dash: null }, { c: color, dash: null }],
};
export function StatusSparkline({
  values,
  splitAt = null,          // 구간 경계 인덱스(첫 구간의 마지막 점). null 이면 단일 선
  emphasis = "head",
  color = C.acc,
  mutedColor = C.text3,
  w = 220,
  h = 34,
  pad = 3,
}) {
  const all = (values || []).map(Number).filter(v => !isNaN(v));
  if (all.length < 2) return null;
  const mn = Math.min(...all), mx = Math.max(...all);
  const span = mx - mn || 1;
  const x = (i) => pad + (i / (all.length - 1)) * (w - 2 * pad);
  const y = (v) => h - pad - ((v - mn) / span) * (h - 2 * pad);
  const pt = (i) => `${x(i).toFixed(1)},${y(all[i]).toFixed(1)}`;
  // 경계 인덱스는 두 구간이 공유한다 — 안 그러면 선이 끊겨 보인다
  const cut = (splitAt == null || splitAt < 0 || splitAt >= all.length - 1) ? null : splitAt;
  const [sA, sB] = (SPARK_EMPHASIS[emphasis] || SPARK_EMPHASIS.head)(color, mutedColor);
  const idxA = cut == null ? all.map((_, i) => i) : all.map((_, i) => i).slice(0, cut + 1);
  const idxB = cut == null ? [] : all.map((_, i) => i).slice(cut);
  const line = (idx) => idx.map(pt).join(" ");
  return (
    <svg width={w} height={h} style={{ display: "block" }}>
      <polyline points={line(idxA)} fill="none" stroke={sA.c} strokeWidth="1.5"
        strokeDasharray={sA.dash || undefined} />
      {idxB.length > 1 && (
        <polyline points={line(idxB)} fill="none" stroke={sB.c} strokeWidth="1.5"
          strokeDasharray={sB.dash || undefined} />
      )}
    </svg>
  );
}


// ── 시계열 차트 (현황 분석 탭 공용) ──────────────────────────────────────────
//   DP/IP/RP/MP 모듈이 공유. trend = { data:[{<x축키>, <계열키>...}], series:[{key, name_key}] }
//   colorMap: 계열키→색, dashKeys: 점선 처리할 키 배열, emptyText: 데이터 없음 문구(t() 처리해 전달)
//   xKey: x축 필드명. 기본 "month"(월별) — MP 처럼 일자별이면 "date" 를 넘긴다.
//   barKeys: 막대로 그릴 키 배열(나머지는 라인). barStackId 를 주면 해당 막대들이 누적된다.
//   height: 차트 높이(px). 전체폭 배치는 기본 140, 좌우 2열 카드로 배치할 땐 240 정도로 키운다.
//   unit: 값의 단위(t() 처리해 전달). 주면 Y축 라벨 + 툴팁 값에 붙는다. 미지정이면 기존과 동일.
export const FORECAST_ACTUAL_DEFAULT_COLORS = {
  actual_qty: C.acc, forecast_qty: C.g1, plan_qty: C.g2,
};
export function ForecastActualChart({
  trend,
  colorMap = FORECAST_ACTUAL_DEFAULT_COLORS,
  dashKeys = ["forecast_qty"],
  emptyText,
  xKey = "month",
  barKeys = [],
  barStackId,
  height = 140,
  unit,
}) {
  const { t } = useTranslation();  // 언어 전환 시 계열 라벨 재렌더
  const data   = trend?.data || [];
  const series = trend?.series || [];
  // 축 눈금은 로케일 기준 축약(1.2만/1.2M 등), 툴팁은 원 수치 그대로
  const fmtAxis = (v) => {
    if (v == null || isNaN(v)) return "";
    try {
      return new Intl.NumberFormat(i18n.language, { notation: "compact", maximumFractionDigits: 1 }).format(v);
    } catch { return Number(v).toLocaleString(); }
  };
  // 툴팁은 원 수치 + 단위. 단위 문자열은 접미사 규약이라(en=" cases") 그대로 붙이고, 축 라벨만 trim.
  const fmtFull = (v) => (v == null || isNaN(v) ? "-" : `${Number(v).toLocaleString()}${unit || ""}`);
  if (!data.length || !series.length) {
    return <Typography variant="caption" color="text.disabled">{emptyText}</Typography>;
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      {/* 단위 라벨은 축 위에 가로로 얹히므로 그만큼 상단 여백을 준다(없으면 첫 눈금에 겹친다) */}
      <ComposedChart data={data} margin={{ top: unit ? 20 : 8, right: 12, bottom: 4, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
        <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: C.text2 }} tickMargin={6}
          minTickGap={24}
          axisLine={{ stroke: C.border }} tickLine={false} />
        {/* 단위는 표준 차트(ChartComposed)와 같이 축 상단에 가로로 — 회전 라벨은 표준이 아니다 */}
        <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: C.text2 }} width={48}
          axisLine={false} tickLine={false}
          label={unit ? { value: String(unit).trim(), angle: 0, dy: -6, position: "top",
                          fontSize: 10, fill: C.text3 } : undefined} />
        <RTooltip
          formatter={(v, name) => [fmtFull(v), name]}
          content={<AgStyleTooltipContent />} />
        {/* iconType 을 지정하지 않는다 — Legend 의 iconType 은 계열별 legendType 을 덮어써서
            막대까지 선으로 그린다. 표준 차트(ChartComposed)처럼 계열 종류가 모양을 정하게 둔다. */}
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {/* isAnimationActive={false} — 탭을 오갈 때마다 컴포넌트가 리마운트돼 그리기
            애니메이션이 매번 재생되면 거슬린다. 대시보드 차트들과 동일한 처리. */}
        {series.map((s) => (barKeys.includes(s.key) ? (
          // 막대는 Legend 전역 iconType(plainline)을 덮어써야 선이 아닌 사각형으로 표시된다
          <Bar key={s.key} dataKey={s.key}
            name={t(s.name_key)} fill={colorMap[s.key] || C.text3}
            stackId={barStackId} legendType="rect" isAnimationActive={false} />
        ) : (
          // type 을 주지 않으면 recharts 기본 'linear' — 각진 꺾은선.
          // monotone(스플라인)은 없는 중간값을 있는 것처럼 보이게 해 계획/실적 판독을 흐린다.
          // BF 모델별 품목 분포 차트와 같은 규칙이니 monotone 으로 되돌리지 말 것.
          // legendType=plainline — 선 계열은 점 없는 실선 표식 유지(Legend 의 iconType 제거 전과 동일)
          <Line key={s.key} dataKey={s.key} legendType="plainline"
            name={t(s.name_key)} stroke={colorMap[s.key] || C.text3}
            strokeWidth={2} connectNulls isAnimationActive={false}
            dot={{ r: 3, fill: colorMap[s.key] || C.text3, strokeWidth: 0 }}
            strokeDasharray={dashKeys.includes(s.key) ? "5 3" : undefined} />
        )))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
